'use client';

/**
 * SysObeyaHighCcFunctionsTableCard — per-function complexity detail.
 *
 * Slices `response.data[0].functions[]` from the system_obeya_view
 * high_cc_functions composite call. Sorted by CC descending — worst
 * offenders surface first. Severity badge color resolves against
 * `response.config.{high, critical}` thresholds:
 *   cc >= critical → destructive (red tint)
 *   cc >= high     → warning     (yellow tint)
 *   else           → primary     (just flagged)
 *
 * Typography from `useTypographyBundle('canvas_dashboard')`. Container
 * chrome (severity badge styling, cell padding, hover) is render
 * concern per the mode7 doctrine. Chrome strings via `response.labels`.
 *
 * Click-to-drill-down on rows is deferred to sel:cc_function in a
 * later iter.
 *
 * @see klyde_edge/ui_config/composition/cards/system_obeya.py — content_key="sys_obeya_high_cc_functions_table"
 * @see klyde_edge/ui_config/labels/system_obeya.py — high_cc_functions_table_*, high_cc_col_*
 */

import { useState, useMemo } from 'react';
import { Code2, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { type TypographyBundleSlots } from '@/lib/design/typography';
import { useCardTypography } from '@/lib/design/card-chrome';
import { useCardExport } from '@/lib/design/card-export';
import { useSystemObeyaView, type SystemObeyaLabels } from './use-system-obeya-view';

interface Props {
  view_name?: string;
}

type SortDir = 'asc' | 'desc';
type Align = 'left' | 'right';

interface ColumnDef {
  key: string;
  label_key: string;
  align: Align;
}

const COLUMNS: ColumnDef[] = [
  { key: 'function_name', label_key: 'high_cc_col_function',   align: 'left'  },
  { key: 'package',       label_key: 'high_cc_col_module',     align: 'left'  },
  { key: 'cc_value',      label_key: 'high_cc_col_cc',         align: 'right' },
  { key: 'severity',      label_key: 'high_cc_col_severity',   align: 'left'  },
  { key: 'suggestion',    label_key: 'high_cc_col_suggestion', align: 'left'  },
];

export function SysObeyaHighCcFunctionsTableCard({ view_name = 'high_cc_functions' }: Props) {
  const { data, isLoading, queryKey } = useSystemObeyaView(view_name);
  const T = useCardTypography();
  const L: SystemObeyaLabels = data?.labels ?? {};

  const [sortKey, setSortKey] = useState<string>('cc_value');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const rows = useMemo<Record<string, unknown>[]>(() => {
    const first = (data?.data ?? [])[0] as { functions?: Record<string, unknown>[] } | undefined;
    const functions = first?.functions ?? [];
    return [...functions].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const as = String(av ?? '');
      const bs = String(bv ?? '');
      return sortDir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
    });
  }, [data, sortKey, sortDir]);

  // Card export — markdown table of the (sorted) function detail.
  // Suggestions are the most actionable content; included verbatim.
  const title = L.high_cc_functions_table_title ?? '';
  useCardExport(useMemo(() => {
    const headers = COLUMNS.map((c) => L[c.label_key] ?? c.key);
    const md = [
      `| ${headers.join(' | ')} |`,
      `| ${headers.map(() => '---').join(' | ')} |`,
      ...rows.map((row) =>
        `| ${COLUMNS.map((c) => formatFnCellForExport(c, row, L)).join(' | ')} |`,
      ),
    ].join('\n');
    // Scene json mirrors painted cell strings ("40", "critical",
    // "Decompose the monolithic method…"). Backend extras
    // (file_stem, location, etc.) travel in the capture envelope's
    // `payloads` section, keeping the
    // `format(payload.X, labels) == scene.row.X` assertion checkable.
    const visibleRows = rows.map((r) =>
      Object.fromEntries(COLUMNS.map((c) => [c.key, formatFnCellForExport(c, r, L)]))
    );
    return {
      label: title,
      text: rows.map((r) =>
        COLUMNS.map((c) => `${L[c.label_key] ?? c.key}=${formatFnCellForExport(c, r, L)}`).join(', ')
      ).join('\n'),
      markdown: md,
      json: { columns: COLUMNS.map((c) => c.key), rows: visibleRows },
      content_key: 'sys_obeya_high_cc_functions_table',
      surface: 'canvas',
      state: { sort_key: sortKey, sort_dir: sortDir },
      // Status = paintable content, not the query flag.
      status: (data ? 'loaded' : 'loading') as 'loading' | 'loaded',
      query_key: queryKey,
    };
  }, [rows, L, title, sortKey, sortDir, data, isLoading, queryKey]));

  if (isLoading || !data) {
    return (
      <CardShell>
        <div className="p-6">
          <div className={T.body}>{L.loading_message ?? ''}</div>
        </div>
      </CardShell>
    );
  }

  const config = (data.config ?? {}) as { high?: number; critical?: number };
  const highThreshold = config.high ?? 20;
  const criticalThreshold = config.critical ?? 30;

  return (
    <CardShell>
      <div className="p-6 border-b border-border/50">
        <div className="flex items-center gap-2">
          <Code2 className="w-5 h-5 text-primary" />
          <h2 className={T.header}>{L.high_cc_functions_table_title ?? ''}</h2>
        </div>
      </div>
      <div className="overflow-auto flex-1">
        <table className="w-full">
          <thead className="sticky top-0 bg-card">
            <tr className="border-b border-border/50">
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  onClick={() => {
                    if (sortKey === col.key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                    else { setSortKey(col.key); setSortDir('desc'); }
                  }}
                  className={cn(
                    T.metadata,
                    'uppercase tracking-wider font-semibold px-3 py-3 cursor-pointer hover:text-primary select-none',
                    col.align === 'right' ? 'text-right' : 'text-left',
                  )}
                >
                  <span className="inline-flex items-center gap-1">
                    {L[col.label_key] ?? ''}
                    {sortKey === col.key && (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={`${row.function_name ?? row.name ?? idx}`} className="border-b border-border/40 hover:bg-muted/30 transition-colors">
                {COLUMNS.map((col) => (
                  <Cell
                    key={col.key}
                    col={col}
                    row={row}
                    T={T}
                    L={L}
                    highThreshold={highThreshold}
                    criticalThreshold={criticalThreshold}
                  />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CardShell>
  );
}

function Cell({
  col,
  row,
  T,
  L,
  highThreshold,
  criticalThreshold,
}: {
  col: ColumnDef;
  row: Record<string, unknown>;
  T: TypographyBundleSlots;
  L: SystemObeyaLabels;
  highThreshold: number;
  criticalThreshold: number;
}) {
  const dash = L.dash_placeholder ?? '—';

  if (col.key === 'function_name') {
    const fnName = (row.function_name as string) || (row.name as string) || '';
    const fileStem = (row.file_stem as string) || '';
    return (
      <td className={cn('px-3 py-2.5', col.align === 'right' ? 'text-right' : 'text-left')}>
        <div className={cn(T.name, 'font-mono')}>{fnName || dash}</div>
        {fileStem && (
          <div className={cn(T.metadata, 'font-mono truncate max-w-[50ch]')}>{fileStem}</div>
        )}
      </td>
    );
  }

  if (col.key === 'package') {
    const pkg = (row.package_display_name as string) || (row.package as string) || '';
    return (
      <td className={cn('px-3 py-2.5', col.align === 'right' ? 'text-right' : 'text-left')}>
        <span className={cn(T.body, 'font-mono text-muted-foreground')}>{pkg || dash}</span>
      </td>
    );
  }

  if (col.key === 'cc_value') {
    const cc = row.cc_value;
    return (
      <td className={cn('px-3 py-2.5', col.align === 'right' ? 'text-right' : 'text-left')}>
        <span className={cn(T.body, 'font-mono font-semibold tabular-nums')}>
          {cc == null ? dash : fmt(cc)}
        </span>
      </td>
    );
  }

  if (col.key === 'severity') {
    const severity = (row.severity as string) || '';
    const cc = typeof row.cc_value === 'number' ? row.cc_value : 0;
    const badgeColor =
      cc >= criticalThreshold
        ? 'bg-destructive/20 text-destructive'
        : cc >= highThreshold
          ? 'bg-warning/20 text-warning'
          : 'bg-primary/20 text-primary';
    return (
      <td className={cn('px-3 py-2.5', col.align === 'right' ? 'text-right' : 'text-left')}>
        <span className={cn(T.metadata, 'inline-block px-2 py-0.5 rounded-md font-medium uppercase tracking-wider', badgeColor)}>
          {severity || dash}
        </span>
      </td>
    );
  }

  if (col.key === 'suggestion') {
    const suggestion = (row.suggestion as string) || '';
    return (
      <td className={cn('px-3 py-2.5 align-top', col.align === 'right' ? 'text-right' : 'text-left')}>
        <div
          className={cn(T.body, 'line-clamp-3 max-w-[50ch] leading-snug')}
          title={suggestion || undefined}
        >
          {suggestion || dash}
        </div>
      </td>
    );
  }

  return <td />;
}

function CardShell({ children }: { children: React.ReactNode }) {
  return <div className="rounded-xl border border-border bg-card shadow-sm h-full overflow-hidden flex flex-col">{children}</div>;
}
function fmt(n: unknown): string {
  if (n == null || typeof n !== 'number') return '—';
  return n.toLocaleString();
}

function formatFnCellForExport(col: ColumnDef, row: Record<string, unknown>, L: SystemObeyaLabels): string {
  const dash = L.dash_placeholder ?? '—';
  if (col.key === 'function_name') {
    return (row.function_name as string) || (row.name as string) || dash;
  }
  if (col.key === 'package') {
    return (row.package_display_name as string) || (row.package as string) || dash;
  }
  if (col.key === 'cc_value') {
    return row.cc_value == null ? dash : fmt(row.cc_value);
  }
  if (col.key === 'severity') {
    return (row.severity as string) || dash;
  }
  if (col.key === 'suggestion') {
    // Suggestions may contain pipes / newlines; collapse for table.
    const raw = (row.suggestion as string) || dash;
    return raw.replace(/\|/g, '\\|').replace(/\n/g, ' ');
  }
  return String(row[col.key] ?? dash);
}
